let socket = null;
let socketApi = null;
let reconnectTimer = null;
let reconnectAttempts = 0;
let manualClose = false;

const listeners = new Map();
const pendingMessages = [];
const joinedRooms = new Set();
const MAX_RECONNECT_ATTEMPTS = 5;
const backendUrl = process.env.REACT_APP_SOCKET_URL || "https://api.staging.quimera.ao:4000/api";

const getChatSocketUrl = (token) => {
  const url = new URL("/ws/chat", backendUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("token", token);

  return url.toString();
};

const trigger = (event, data) => {
  const eventListeners = listeners.get(event);

  if (!eventListeners) return;

  eventListeners.forEach((listener) => listener(data));
};

const sendPayload = (event, data = {}) => {
  const payload = JSON.stringify({ event, data });

  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(payload);
    return;
  }

  pendingMessages.push(payload);
};

const flushPendingMessages = () => {
  while (pendingMessages.length > 0 && socket?.readyState === WebSocket.OPEN) {
    socket.send(pendingMessages.shift());
  }
};

const rejoinRooms = () => {
  joinedRooms.forEach((roomId) => {
    sendPayload("join_room", { roomId });
  });
};

const handleSocketMessage = (event) => {
  try {
    const payload = JSON.parse(event.data || "{}");
    const eventName = payload.event;
    const data = payload.data;

    if (!eventName) return;

    trigger(eventName, data);

    if (eventName === "message_received") {
      trigger("receive_message", {
        ...(data?.message || {}),
        roomId: data?.roomId,
      });
    }
  } catch (error) {
    console.error("Chat socket payload error:", error);
  }
};

const scheduleReconnect = () => {
  if (manualClose || reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;

  reconnectAttempts += 1;
  clearTimeout(reconnectTimer);
  reconnectTimer = setTimeout(
    () => {
      socket = null;
      connectSocket();
    },
    Math.min(1000 * reconnectAttempts, 5000),
  );
};

const createSocketApi = () => ({
  close: () => {
    manualClose = true;
    clearTimeout(reconnectTimer);
    socket?.close();
    socket = null;
  },
  emit: sendPayload,
  get readyState() {
    return socket?.readyState;
  },
  joinRoom: (roomId) => {
    if (roomId) {
      joinedRooms.add(String(roomId));
      if (socket?.readyState === WebSocket.OPEN) {
        sendPayload("join_room", { roomId });
      }
    }
  },
  off: (event, listener) => {
    if (!listeners.has(event)) return;

    if (listener) {
      listeners.get(event).delete(listener);
      return;
    }

    listeners.delete(event);
  },
  on: (event, listener) => {
    if (!listeners.has(event)) {
      listeners.set(event, new Set());
    }

    listeners.get(event).add(listener);
  },
  sendMessage: (roomId, message, attachments = []) => {
    sendPayload("send_message", {
      roomId,
      message,
      attachments,
    });
  },
});

export const connectSocket = () => {
  if (typeof window === "undefined") return null;

  if (socketApi && socket && socket.readyState !== WebSocket.CLOSED) {
    return socketApi;
  }

  const token = localStorage.getItem("token");

  if (!token) {
    console.warn("No auth token found for chat socket");
    return null;
  }

  manualClose = false;
  socketApi = socketApi || createSocketApi();
  socket = new WebSocket(getChatSocketUrl(token));

  socket.addEventListener("open", () => {
    reconnectAttempts = 0;
    rejoinRooms();
    flushPendingMessages();
    trigger("connect", null);
  });

  socket.addEventListener("message", handleSocketMessage);

  socket.addEventListener("close", () => {
    trigger("disconnect", null);
    scheduleReconnect();
  });

  socket.addEventListener("error", (error) => {
    trigger("connect_error", error);
  });

  return socketApi;
};

export const getSocket = () => socketApi || connectSocket();
